<template>
  <div class="review-step">
    <div class="text-center mb-2 border-bottom">
      <h2 class="mb-1">Référence : <strong>{{ opportunityReference }}</strong></h2>
    </div>

    <b-card v-if="budgetTracking" no-body class="mb-2">
      <b-card-body>
        <h5 class="mb-1">Détails du devis</h5>

        <!-- Services -->
        <div v-if="budgetTracking.services && budgetTracking.services.length" class="mb-2">
          <h6 class="text-muted mb-1">Services</h6>
          <b-list-group>
            <b-list-group-item
              v-for="(service, index) in budgetTracking.services"
              :key="index"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <strong>{{ service.designation }}</strong>
                <div class="text-muted small">
                  {{ service.qty }} * {{ service.unit }} <i v-if="service.tax">({{ service.tax }})</i>
                </div>
              </div>
              <b-badge variant="primary">{{ amountToCurrency(service.total) }}</b-badge>
            </b-list-group-item>
          </b-list-group>
        </div>

        <!-- Totaux -->
        <div class="border-top pt-1">
          <div class="d-flex justify-content-between mb-50">
            <span>Montant HT</span>
            <strong>{{ amountToCurrency(calculateDutiesFree()) }}</strong>
          </div>
          <div class="d-flex justify-content-between mb-50">
            <span>Accompte à payer ({{ budgetTracking.percent_deposit }}%)</span>
            <strong>{{ amountToCurrency(calculateDeposit()) }}</strong>
          </div>
          <div class="d-flex justify-content-between text-primary">
            <h5>Total TTC</h5>
            <h5>{{ amountToCurrency(calculateTotal()) }}</h5>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Dialog de rejet -->
    <b-modal
      v-model="showRejectDialog"
      title="Rejeter le devis"
      cancel-title="Annuler"
      ok-title="Confirmer le rejet"
      ok-variant="danger"
      @ok="handleReject"
    >
      <b-form-group
        label="Motif du rejet"
        label-for="reject-comment"
        description="Minimum 5 caractères"
      >
        <b-form-textarea
          id="reject-comment"
          v-model="rejectComment"
          placeholder="Expliquez pourquoi vous rejetez ce devis..."
          rows="4"
          :state="rejectComment.length >= 5 ? null : false"
        />
      </b-form-group>
    </b-modal>

    <!-- Actions -->
    <b-row>
      <b-col cols="12" md="6" class="mb-1 mb-md-0">
        <b-button
          variant="danger"
          block
          :disabled="isLoading"
          @click="showRejectDialog = true"
        >
          <feather-icon icon="XIcon" class="mr-50" />
          Rejeter
        </b-button>
      </b-col>
      <b-col cols="12" md="6">
        <b-button
          variant="success"
          block
          :disabled="isLoading"
          @click="handleApprove"
        >
          <b-spinner v-if="isLoading" small class="mr-1" />
          <feather-icon v-else icon="CheckIcon" class="mr-50" />
          Approuver
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BListGroup,
  BListGroupItem,
  BBadge,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BModal,
  BFormGroup,
  BFormTextarea,
} from "bootstrap-vue";
import { ref } from "@vue/composition-api";
import {amountToCurrency} from "@/utils/formatAmount";

export default {
  name: "QuoteValidationStepReview",
  methods: {amountToCurrency},
  components: {
    BCard,
    BCardBody,
    BListGroup,
    BListGroupItem,
    BBadge,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BModal,
    BFormGroup,
    BFormTextarea,
  },
  props: {
    budgetTracking: {
      type: Object,
      required: true,
    },
    opportunityReference: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const showRejectDialog = ref(false);
    const rejectComment = ref("");

    const calculateDutiesFree = () => {
      if (!props.budgetTracking?.services) return 0;
      return props.budgetTracking.services.reduce(
        (sum, service) => sum + (parseFloat(service.duty_free) || 0),
        0
      );
    };

    const calculateTotal = () => {
      if (!props.budgetTracking?.services) return 0;
      return props.budgetTracking.services.reduce(
        (sum, service) => sum + (parseFloat(service.total) || 0),
        0
      );
    };

    const calculateDeposit = () => {
      const total = calculateTotal();
      const percent = parseFloat(props.budgetTracking?.percent_deposit) || 0;
      return (total * percent) / 100;
    };

    const handleApprove = () => {
      emit("approve");
    };

    const handleReject = (bvModalEvent) => {
      bvModalEvent.preventDefault();
      if (rejectComment.value.length >= 5) {
        emit("reject", rejectComment.value);
        showRejectDialog.value = false;
        rejectComment.value = "";
      }
    };

    return {
      showRejectDialog,
      rejectComment,
      calculateDutiesFree,
      calculateTotal,
      calculateDeposit,
      handleApprove,
      handleReject,
    };
  },
};
</script>

<style scoped>
.review-step {
  max-width: 600px;
  margin: 0 auto;
}
</style>
